/**
 * Poll Thunks
 *
 * Async thunk for polling async task results (e.g., flight search).
 * Handles abort gates, network retries, and thinking message updates.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { PollHttpMethod, PollResponse } from '@contracts';
import { fetchPollResult, PollApiError } from '../api/pollApi';
import {
  appendPollResultMessage,
  completeStreaming,
  addThinkingMessage,
  clearThinkingMessages,
  setStreamingError,
} from '../slices/messagesSlice';
import { updateTemplateFromStream } from '../slices/templateSlice';
import { showFlightsErrorPopup } from '@/features/flights/redux';
import { registerPollAbortController, abortCurrentPoll } from '../../utils/streamAbortManager';
import { abortableSleep, isAbortError } from '@/shared/utils/abortUtils';
import { logger } from '@/shared/utils/logger';
import type { AppDispatch } from '@/store/store';
import { CHAT_ENDPOINTS } from '@/api/endpoints';

// ============================================
// Constants
// ============================================

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_MAX_ATTEMPTS = 10;
const MAX_NETWORK_RETRIES = 3;
const NETWORK_RETRY_DELAY_MS = 1000;

// ============================================
// Types
// ============================================

interface PollAsyncTaskParams {
  pollEndpoint: string;
  pollMethod: PollHttpMethod;
  params: Record<string, string>;
  pollIntervalMs?: number;
  maxAttempts?: number;
  maxNetworkRetries?: number;
  networkRetryDelayMs?: number;
  messageId: string;
}

// ============================================
// Dispatch Helpers
// ============================================

function finishWithError(dispatch: AppDispatch, message: string): void {
  dispatch(clearThinkingMessages());
  dispatch(setStreamingError(message));
}

function finishWithResponse(
  dispatch: AppDispatch,
  response: PollResponse & { status: 'COMPLETED' },
): void {
  dispatch(clearThinkingMessages());
  dispatch(appendPollResultMessage(response.response));
  dispatch(updateTemplateFromStream(response.response));
  dispatch(completeStreaming());
}

function finishWithFailedResponse(
  dispatch: AppDispatch,
  response: PollResponse & { status: 'FAILED' },
): void {
  dispatch(clearThinkingMessages());
  dispatch(appendPollResultMessage(response.response));
  dispatch(completeStreaming());
}

/**
 * Wait for an interval, returning false if aborted.
 */
async function waitOrAbort(ms: number, signal: AbortSignal): Promise<boolean> {
  try {
    await abortableSleep(ms, signal);
    return !signal.aborted;
  } catch {
    return false;
  }
}

// ============================================
// Thunk
// ============================================

/**
 * Poll for async task results.
 *
 * Called when chat stream FINAL_RESPONSE contains an asyncTask.
 * Polls the specified endpoint until COMPLETED, FAILED, max attempts, or abort.
 *
 * Abort gates after every `await` prevent stale dispatches when user sends a new message.
 */
export const pollAsyncTaskThunk = createAsyncThunk<
  void,
  PollAsyncTaskParams,
  { dispatch: AppDispatch }
>('chat/pollAsyncTask', async (params, { dispatch }) => {
  const {
    pollMethod,
    params: pollParams,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    maxNetworkRetries = MAX_NETWORK_RETRIES,
    networkRetryDelayMs = NETWORK_RETRY_DELAY_MS,
  } = params;

  const pollEndpoint = CHAT_ENDPOINTS.POLL_RESULT;
  const abortController = new AbortController();
  registerPollAbortController(abortController);
  const { signal } = abortController;

  let attempts = 0;
  let consecutiveNetworkErrors = 0;
  let lastCbError: PollApiError | null = null;

  try {
    while (attempts < maxAttempts && !signal.aborted) {
      attempts += 1;
      logger.debug(`[pollAsyncTask] Attempt ${attempts}/${maxAttempts}`);

      const { response: result, cbError } = await fetchSafely(
        pollEndpoint,
        pollParams,
        pollMethod,
        signal,
      );

      if (signal.aborted) return;

      if (cbError) {
        lastCbError = cbError;
      }

      if (result === null) {
        if (!cbError) {
          consecutiveNetworkErrors += 1;
          logger.warn(
            `[pollAsyncTask] Network error (${consecutiveNetworkErrors}/${maxNetworkRetries})`,
          );

          if (consecutiveNetworkErrors >= maxNetworkRetries) {
            finishWithError(dispatch, 'Connection lost. Please try again.');
            return;
          }
        }

        if (!(await waitOrAbort(cbError ? pollIntervalMs : networkRetryDelayMs, signal))) return;
        continue;
      }

      consecutiveNetworkErrors = 0;

      switch (result.status) {
        case 'PROCESSING': {
          if (result.message) {
            dispatch(clearThinkingMessages());
            dispatch(addThinkingMessage(result.message));
          }
          if (!(await waitOrAbort(pollIntervalMs, signal))) return;
          continue;
        }

        case 'COMPLETED': {
          if (signal.aborted) return;
          finishWithResponse(dispatch, result);
          return;
        }

        case 'FAILED': {
          if (signal.aborted) return;
          finishWithFailedResponse(dispatch, result);
          return;
        }
      }
    }

    if (!signal.aborted) {
      finishWithError(dispatch, 'Search timed out. Please try again.');
      if (lastCbError) {
        dispatch(
          showFlightsErrorPopup({
            errorMessage: lastCbError.errorMessage,
            errorDescription: lastCbError.errorDescription,
          }),
        );
      }
    }
  } catch (error: unknown) {
    if (isAbortError(error)) return;

    logger.error('[pollAsyncTask] Unexpected error:', error);
    if (!signal.aborted) {
      finishWithError(dispatch, 'Search failed. Please try again.');
    }
  } finally {
    abortCurrentPoll();
  }
});

// ============================================
// Network Fetch Helper
// ============================================

interface FetchSafelyResult {
  response: PollResponse | null;
  cbError: PollApiError | null;
}

/**
 * Fetch poll result, returning null on error instead of throwing.
 * CB errors (PollApiError) are captured and returned for the caller to track.
 * Abort errors are re-thrown to stop the loop.
 */
async function fetchSafely(
  endpoint: string,
  params: Record<string, string>,
  method: PollHttpMethod,
  signal: AbortSignal,
): Promise<FetchSafelyResult> {
  try {
    const response = await fetchPollResult(endpoint, params, method, signal);
    return { response, cbError: null };
  } catch (error: unknown) {
    if (isAbortError(error)) throw error;
    const cbError = error instanceof PollApiError ? error : null;
    return { response: null, cbError };
  }
}
